import prependRoute from '~/util/prependRoute'
import { cl } from '~/plugins/global.js'
// RESOURCES
// https://support.google.com/webmasters/thread/4048030?hl=en

const schemaContext = 'https://schema.org'
const base = process.env.V2_BASE_URL
const home = base[base.length - 1] === '/' ? base : base + '/'

function generateGraph({ page = {}, lang = 'en', route = {} }) {
    // console.log(route)
    const data = { page, lang }
    const graph = []
    // Set Organization on Homepage only
    if (page && page.slug && page.slug.en && page.slug.en.current === 'home') {
        graph.push(setOrganization(data))
        graph.push(setWebSite(data))
    }
    graph.push(setOrganization({ page, lang, context: '/site' }))
    graph.push(setPerson(data)) // Author
    graph.push(setWebSite({ page, lang, context: '/site' }))
    if (page._type === 'product') {
        let pn = ''
        if (route.pn && route.pn1 && route.pn2) {
            pn = `${route.pn}/${route.pn1}/${route.pn2}`
        } else if (route.pn && route.pn1 && !route.pn2) {
            pn = `${route.pn}/${route.pn1}`
        } else {
            pn = route.pn
        }
        graph.push(setProduct({ page, lang, pn }))
    } else {
        graph.push(setWebPage(data))
    }

    // graph.push(setImageObjects())
    return graph
}

function setOrganization({ page = {}, lang = 'en', context = '' }) {
    return {
        '@id': `${home}#organization${context}`,
        '@context': schemaContext,
        '@type': 'Organization',
        url: home,
        name: 'c3controls',
        description: getOrganizationDesc({ page, lang }),
        foundingDate: '1976',
        founder: {
            '@type': 'Person',
            name: 'Glenn Taylor'
        },
        address: {
            '@type': 'PostalAddress',
            addressCountry: 'United States',
            addressLocality: 'Beaver',
            addressRegion: 'PA',
            postalCode: '15009',
            streetAddress: '664 State Street'
        },
        contactPoint: [
            {
                '@type': 'ContactPoint',
                telephone: '+1-724-775-7926',
                contactType: 'customer service',
                areaServed: 'Worldwide',
                availableLanguage: ['English', 'Spanish']
            }
        ],
        logo: {
            '@context': schemaContext,
            '@type': 'ImageObject',
            url:
                'https://img.c3controls.com/c3controls-logo.jpg',
            width: 1641,
            height: 196
        },
        sameAs: [
            'https://www.facebook.com/c3controls/',
            'https://www.linkedin.com/company/c3controls/',
            'https://twitter.com/c3controls',
            'https://www.youtube.com/user/c3controls/'
        ]
    }
}

// https://staging.c3controls.tech/

function setPerson({ page = {}, lang = 'en' }) {
    return {
        '@id': `${home}article/ted-wodoslawsky-director-commercial-marketing/#person`,
        '@context': schemaContext,
        '@type': 'Person',
        name: 'Ted Wodowslawsky'
    }
}

function setWebSite({ page = {}, lang = 'en', context = '' }) {
    return {
        '@id': `${home}#website${context}`,
        '@context': schemaContext,
        '@type': 'WebSite',
        url: home,
        name: 'c3controls.com',
        description: getOrganizationDesc({ page, lang }),
        headline: getOrganizationTitle({ page, lang }),
        inLanguage: getInLanguage({ lang }),
        copyrightYear: '2016',
        dateCreated: '2016-12-01T17:59:27+00:00',
        datePublished: '2016-12-01T17:59:27+00:00',
        dateModified: '2020-03-30T21:24:27+00:00',
        publisher: getPublisher({ page, lang }),
        author: getAuthor({ page, lang }),
        thumbnailUrl:
            'https://live-c3c.pantheonsite.io/wp-content/uploads/2020/03/c3controls-we-are-here-for-you-6-600x315-cropped.jpg',
        // image: [
        //     {
        //         '@id':
        //             'https://live-c3c.pantheonsite.io/wp-content/uploads/2019/11/c3controls-news-Circuit-Breaker-or-Fuse-Whats-the-Difference-800x397.jpg#id/image.object'
        //     }
        // ],
        potentialAction: {
            '@type': 'SearchAction',
            target: `${home}search?q={search_term_string}`,
            'query-input': 'required name=search_term_string'
        }
    }
}

function getRouteData({ page = {}, lang = 'en' }) {
    const route = prependRoute({ type: page._type, slug: page.slug })
    const url = route[lang] ? `${base}${route[lang]}` : `${home}`
    let type = 'WebPage'
    if (page._type === 'article' || page._type === 'whitepaper') {
        type = 'Article'
    } else if (page._type === 'product' || page._type === 'singleProduct') {
        type = 'Product'
    }
    const id = `${url}#${type.toLowerCase()}`
    return { route, url, type, id }
}

function setWebPage({ page = {}, lang = 'en' }) {
    const { url, type, id } = getRouteData({ page, lang })
    return {
        '@id': `${id}`,
        '@context': schemaContext,
        '@type': type,
        name: getTitle({ page, lang }),
        headline: getTitle({ page, lang }),
        description: getDescription({ page, lang }),
        publisher: getPublisher({ page, lang }),
        mainEntityOfPage: `${url}`,
        url: `${url}`,
        inLanguage: getInLanguage({ lang }),
        copyrightYear: getPublishYear({ page, lang }),
        dateCreated: getPublishDate({ page, lang }),
        datePublished: getPublishDate({ page, lang }),
        dateModified: getModifiedDate({ page, lang }),
        image: getThumbnail({ page, lang }),
        isPartOf: {
            '@id': `${home}#website/site`
        },
        author: {
            '@id': `${home}article/ted-wodoslawsky-director-commercial-marketing/#person`
        }
    }
}

// @TODO Consider use of https://schema.org/SomeProducts
function setProduct({ page = {}, lang = 'en', pn = '' }) {
    const { url, id } = getRouteData({ page, lang })
    return {
        '@id': `${id}`,
        '@context': schemaContext,
        '@type': 'Product',
        name: getTitle({ page, lang }),
        alternateName: getMetaTitle({ page, lang }),
        description: getDescription({ page, lang }),
        mainEntityOfPage: `${url}`,
        url: `${url}`,
        brand: {
            '@context': schemaContext,
            '@type': 'Brand',
            name: 'c3controls'
        },
        itemCondition: 'NewCondition',
        category: getCategory({ page, lang }),
        mpn: getSKU({ page, lang, pn }),
        sku: getSKU({ page, lang, pn }),
        image: getThumbnail({ page, lang }),
        offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: '9.99',
            url: `${url}`,
            priceValidUntil: `${getPublishYear({ page, lang })}-12-31`,
            itemCondition: `${schemaContext}/NewCondition`,
            availability: `${schemaContext}/InStock`,
            seller: getPublisher({ page, lang })
        }
        // isPartOf: {
        //     '@id': `${home}#website/site`
        // }
        // headline: getTitle({ page, lang }),
        // publisher: getPublisher({ page, lang }),
        // author: {
        //     '@id': `${home}team/ted-wodoslawsky/#person`
        // }
        // copyrightYear: getPublishYear({ page, lang }),
        // dateCreated: getPublishDate({ page, lang }),
        // datePublished: getPublishDate({ page, lang }),
        // dateModified: getModifiedDate({ page, lang }),
        // inLanguage: getInLanguage({ lang }),
    }
}

function getSKU({ page = {}, lang = 'en', pn = '' }) {
    if (!pn)
        return page.slug && page.slug.en && page.slug.en.current
            ? page.slug.en.current
            : 'c3controls'
    return pn
}

function getCategory({ page = {}, lang = 'en' }) {
    // @TODO Better handling of categories here
    const cats = page.categories || []
    if (cats.length > 0) {
        // console.log('cats', cats)
        const main = cats.filter((item) => {
            return item.isFeatured
        })
        // console.log('main', main[0].title.en)
        if (main[0] && main[0].title && main[0].title.en)
            return main[0].title.en
    }
    return 'c3controls'
}

function getOrganizationTitle({ page = {}, lang = 'en' }) {
    // TODO: return the default from sanity settings
    return 'c3controls &ndash; Global Leader in Industrial Control Devices'
}
function getOrganizationDesc({ page = {}, lang = 'en' }) {
    // TODO: return the default from sanity settings
    return 'We design and manufacture standard industrial control products that meet global industry specifications for quality &amp; performance. All products carry a limited lifetime warrany and guaranteed same-day shipping on orders.'
}

function getTitle({ page = {}, lang = 'en' }) {
    // generally return the page title, there may be exceptions
    return page && page.title && page.title[lang]
        ? page.title[lang]
        : getOrganizationTitle({ page, lang })
}

function getMetaTitle({ page = {}, lang = 'en' }) {
    // generally return the meta description if there is one
    const fallback =
        page && page.title && page.title[lang]
            ? page.title[lang]
            : getOrganizationTitle({ page, lang })
    return page.seometa &&
        page.seometa.metaTitle &&
        page.seometa.metaTitle[lang]
        ? page.seometa.metaTitle[lang]
        : fallback
}

function getDescription({ page = {}, lang = 'en' }) {
    // generally return the meta description if there is one
    return page.seometa &&
        page.seometa.metaDescription &&
        page.seometa.metaDescription[lang]
        ? page.seometa.metaDescription[lang]
        : getOrganizationDesc({ page, lang })
}

function getPublisher({ page = {}, lang = 'en' }) {
    return {
        '@id': `${home}#organization/site`
    }
}

function getPublishYear({ page = {}, lang = 'en' }) {
    if (!page.publishDate) {
        return '2020'
    }
    return page.publishDate.substring(0, 4)
}
function getPublishDate({ page = {}, lang = 'en' }) {
    if (!page.publishDate) {
        return '2020-01-01T00:00:00+00:00'
    }
    return `${page.publishDate}T00:00:00+00:00`
}

function getModifiedDate({ page = {}, lang = 'en' }) {
    if (!page._updatedAt) {
        return getPublishDate({ page, lang })
    }
    return page._updatedAt
}

function getThumbnail({ page = {}, lang = 'en' }) {
    let idString = 'logos/c3controls-icon'
    if (
        page.seometa &&
        page.seometa.openGraphImage &&
        page.seometa.openGraphImage.imageId &&
        page.seometa.openGraphImage.imageDir
    ) {
        idString = `${page.seometa.openGraphImage.imageDir}/${page.seometa.openGraphImage.imageId}`
    } else if (
        page.thumbnail &&
        page.thumbnail.imageId &&
        page.thumbnail.imageDir
    ) {
        idString = `${page.thumbnail.imageDir}/${page.thumbnail.imageId}`
    }
    return cl.url(idString, {
        width: 1200,
        fetch_format: 'auto',
        flags: 'lossy',
        quality: 'auto'
    })
}

function getAuthor({ page = {}, lang = 'en' }) {
    return {
        '@id': `${home}article/ted-wodoslawsky-director-commercial-marketing/#person`
    }
}

function getInLanguage({ lang = 'en' }) {
    return lang === 'es' ? 'es_SV' : 'en_US'
}

export { generateGraph }
